import { Clock, Effect } from 'effect';
import { ClientHttpError, type OkraClient } from '../client';
import { OkraRuntimeError } from '../errors';
import type { OkraJob } from '../types';
import { formatProgressLine } from './output';

const terminalStatuses = new Set([
  'completed',
  'completed_with_errors',
  'failed',
  'cancelled',
  'succeeded',
]);
const isDocumentId = (value: string) => /^doc[-_][A-Za-z0-9_-]+$/.test(value);
const failure = (method: string, cause: unknown) => new ClientHttpError({ method, cause });

export const waitForJobEffect = Effect.fn('cli.jobs.wait')(function* (
  client: OkraClient,
  id: string,
  options: { timeoutMs: number; onProgress?: (line: string) => void },
) {
  // Let the runtime attach caller interruption before acquiring a foreign request.
  yield* Effect.yieldNow;
  let jobId: string | undefined = isDocumentId(id) ? undefined : id;
  let latest: OkraJob | undefined;
  const startedAt = yield* Clock.currentTimeMillis;
  const timeoutError = () =>
    failure(
      'job_wait',
      new OkraRuntimeError(
        'TIMEOUT',
        jobId
          ? `Timed out waiting for job ${jobId} after ${options.timeoutMs}ms`
          : `Timed out resolving document.parse job for ${id} after ${options.timeoutMs}ms`,
        504,
        {
          ...(jobId ? { job_id: jobId } : {}),
          document_id: latest?.document_id ?? (isDocumentId(id) ? id : undefined),
          status: latest ? String(latest.status) : 'unknown',
          status_url:
            latest?.status_url ??
            (jobId
              ? `/v1/jobs/${encodeURIComponent(jobId)}`
              : `/v1/jobs?type=document.parse&document_id=${encodeURIComponent(id)}&limit=1`),
        },
      ),
    );
  const wait = Effect.gen(function* () {
    if (jobId === undefined) {
      const jobs = yield* client.listJobsEffect({
        documentId: id,
        type: 'document.parse',
        limit: 1,
      });
      jobId = jobs.data[0]?.id;
      if (!jobId)
        return yield* failure(
          'job_wait',
          new OkraRuntimeError('INVALID_REQUEST', `No document.parse job found for ${id}`, 404, {
            document_id: id,
          }),
        );
    }
    while (true) {
      if ((yield* Clock.currentTimeMillis) - startedAt >= options.timeoutMs)
        return yield* timeoutError();
      latest = yield* client.getJobEffect(jobId);
      const job = latest;
      const status = String(job.status);
      if (terminalStatuses.has(status)) {
        if (status === 'failed' || status === 'cancelled') {
          const rawReason =
            job.user_message ||
            job.error ||
            job.latest_error ||
            job.last_error?.message ||
            job.error_code ||
            null;
          const reason =
            rawReason && rawReason.length > 300 ? `${rawReason.slice(0, 297)}…` : rawReason;
          const message = reason
            ? `Job ${jobId} ${status}: ${reason}`
            : `Job ${jobId} ended with status ${status}`;
          const docId = job.document_id ?? (isDocumentId(id) ? id : undefined);
          return yield* failure(
            'job_wait',
            new OkraRuntimeError('EXTRACTION_FAILED', message, 500, {
              ...job,
              next_actions: [
                {
                  cmd: `okra jobs get ${jobId}`,
                  why: 'Inspect the full job payload — error, error_code, recovery_hint.',
                },
                ...(docId
                  ? [
                      {
                        cmd: `okra parse ${docId}`,
                        why: 'Reparse the document; add --engine <name> to try a different OCR vendor.',
                      },
                    ]
                  : []),
              ],
            }),
          );
        }
        return job;
      }
      const now = yield* Clock.currentTimeMillis;
      yield* Effect.try({
        try: () => options.onProgress?.(formatProgressLine(`Job ${jobId}`, status, startedAt, now)),
        catch: (cause) => failure('job_progress', cause),
      });
      yield* Effect.sleep(2000);
    }
  });
  return yield* wait.pipe(
    Effect.timeoutOrElse({
      duration: options.timeoutMs,
      orElse: () => Effect.fail(timeoutError()),
    }),
  );
});

export type DocumentWaitForMatch = {
  node_id: string;
  page: number | null;
  value_excerpt: string;
  bbox?: { x: number; y: number; w: number; h: number };
  bbox_source?: string;
};
type SubscriptionWait = { status?: string; matched?: boolean; matches?: DocumentWaitForMatch[] };

export const waitForDocumentMatchEffect = Effect.fn('cli.documents.waitFor')(function* (
  client: Pick<OkraClient, 'requestEffect'>,
  documentId: string,
  options: {
    match: string;
    literal?: boolean;
    timeoutMs: number;
    onProgress?: (line: string) => void;
  },
) {
  yield* Effect.yieldNow;
  const match = options.match;
  const kind = options.literal ? 'literal' : 'fts';
  let subscriptionId: string | undefined;
  // The command deadline includes registration. Once accepted, the subscription is durable.
  const startedAt = yield* Clock.currentTimeMillis;
  const timeoutError = () =>
    failure(
      'subscription_wait',
      new OkraRuntimeError(
        'TIMEOUT',
        subscriptionId
          ? `Timed out waiting for document ${documentId} subscription ${subscriptionId} after ${options.timeoutMs}ms`
          : `Timed out creating document ${documentId} subscription after ${options.timeoutMs}ms`,
        504,
        {
          document_id: documentId,
          ...(subscriptionId ? { subscription_id: subscriptionId } : {}),
          match,
          kind,
          status: 'timeout',
          status_url: `/v1/documents/${encodeURIComponent(documentId)}/subscriptions${subscriptionId ? `/${encodeURIComponent(subscriptionId)}/wait` : ''}`,
        },
      ),
    );
  const wait = Effect.gen(function* () {
    const created = yield* client.requestEffect<{ subscription_id?: string }>(
      `/v1/documents/${encodeURIComponent(documentId)}/subscriptions`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ match, kind }),
      },
    );
    subscriptionId = created.subscription_id;
    if (!subscriptionId)
      return yield* failure(
        'subscription_wait',
        new OkraRuntimeError(
          'INVALID_RESPONSE',
          'Subscription create response did not include subscription_id',
          502,
          created,
        ),
      );
    while (true) {
      const elapsedMs = (yield* Clock.currentTimeMillis) - startedAt;
      const remainingMs = options.timeoutMs - elapsedMs;
      if (remainingMs <= 0) return yield* timeoutError();
      const requestTimeoutSeconds = Math.min(120, Math.max(1, Math.ceil(remainingMs / 1000)));
      const response = yield* client.requestEffect<SubscriptionWait>(
        `/v1/documents/${encodeURIComponent(documentId)}/subscriptions/${encodeURIComponent(subscriptionId)}/wait?timeout=${requestTimeoutSeconds}`,
        { method: 'GET' },
      );
      const matches = Array.isArray(response.matches) ? response.matches : [];
      if (response.status === 'matched' || response.matched === true || matches.length > 0) {
        return {
          object: 'document_subscription_match' as const,
          document_id: documentId,
          subscription_id: subscriptionId,
          match,
          kind,
          status: 'matched' as const,
          matched_nodes: matches,
        };
      }
      yield* Effect.try({
        try: () =>
          options.onProgress?.(
            `Subscription ${subscriptionId}: waiting for ${JSON.stringify(match)}`,
          ),
        catch: (cause) => failure('subscription_progress', cause),
      });
    }
  });
  return yield* wait.pipe(
    Effect.timeoutOrElse({
      duration: options.timeoutMs,
      orElse: () => Effect.fail(timeoutError()),
    }),
  );
});
